import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Table,
  TableHeader,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Trash, Phone, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Lead, LeadStatus } from "@/types";

interface LeadTableProps {
  leads: Lead[];
  isLoading: boolean;
  activeTab: LeadStatus | "all";
  searchQuery: string;
  selectedLeads: Set<string>;
  handleSelectLead: (id: string, isSelected: boolean) => void;
  handleDeleteLead: (id: string) => void;
  selectAll: boolean;
  handleSelectAllChange: (checked: boolean) => void;
}

const LeadTable = ({ 
  leads, 
  isLoading, 
  activeTab, 
  searchQuery, 
  selectedLeads, 
  handleSelectLead, 
  handleDeleteLead,
  selectAll,
  handleSelectAllChange
}: LeadTableProps) => {
  const filteredLeads = useMemo(() => {
    let filtered = leads;

    if (activeTab !== "all") {
      filtered = filtered.filter((lead) => lead.status === activeTab);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (lead) =>
          lead.name.toLowerCase().includes(query) ||
          lead.email.toLowerCase().includes(query) ||
          lead.phone.toLowerCase().includes(query) ||
          (lead.meta_notes && lead.meta_notes.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [leads, activeTab, searchQuery]);

  const getStatusColor = (status: LeadStatus): string => {
    switch (status) {
      case "queued":
        return "text-gray-500 bg-gray-100/80";
      case "in-progress":
        return "text-blue-500 bg-blue-100/80";
      case "completed":
        return "text-green-500 bg-green-100/80";
      case "reactivated":
        return "text-orange-500 bg-orange-100/80";
      case "not-interested":
        return "text-red-500 bg-red-100/80";
      default:
        return "text-gray-500 bg-gray-100/80";
    }
  };

  const formatStatus = (status: LeadStatus): string => {
    return status.replace("-", " ");
  };

  return (
    <div className="border rounded-md overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]">
              <Checkbox 
                checked={selectAll}
                onCheckedChange={handleSelectAllChange}
                aria-label="Select all leads"
              />
            </TableHead>
            <TableHead>Lead</TableHead>
            <TableHead className="hidden md:table-cell">Contact</TableHead>
            <TableHead className="hidden md:table-cell">Notes</TableHead>
            <TableHead className="w-[120px]">Status</TableHead>
            <TableHead className="w-[100px] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6} className="h-24 text-center">
                <div className="flex justify-center items-center h-full">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              </TableCell>
            </TableRow>
          ) : filteredLeads.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="h-24 text-center">
                {searchQuery ? (
                  <div>
                    <p className="text-muted-foreground">No leads match your search</p>
                    <p className="text-sm text-muted-foreground mt-1">Try a different search term</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-muted-foreground">No leads found</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {activeTab === "all" ? "Add your first lead to get started" : `No leads with status: ${activeTab}`}
                    </p>
                  </div>
                )}
              </TableCell>
            </TableRow>
          ) : (
            filteredLeads.map((lead) => (
              <TableRow key={lead.id} className={lead.id && selectedLeads.has(lead.id) ? "bg-muted/50" : undefined}>
                <TableCell>
                  <Checkbox 
                    checked={lead.id ? selectedLeads.has(lead.id) : false}
                    onCheckedChange={(checked) => {
                      if (lead.id) handleSelectLead(lead.id, !!checked);
                    }}
                    aria-label={`Select ${lead.name}`}
                  />
                </TableCell>
                <TableCell>
                  <div className="font-medium">{lead.name}</div>
                  <div className="text-sm text-muted-foreground md:hidden">{lead.email}</div>
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  <div className="flex flex-col">
                    <span>{lead.email}</span>
                    <span className="text-muted-foreground">{lead.phone}</span>
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  <span className="line-clamp-1">{lead.meta_notes || "No notes"}</span>
                </TableCell>
                <TableCell>
                  <Badge 
                    variant="outline" 
                    className={`${getStatusColor(lead.status)}`}
                  >
                    {formatStatus(lead.status)}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {lead.id && (
                        <>
                          <DropdownMenuItem asChild>
                            <Link to={`/leads/${lead.id}/call-flow`}>
                              <Phone className="mr-2 h-4 w-4" />
                              AI Call Flow
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to={`/calls/${lead.id}`}>
                              <FileText className="mr-2 h-4 w-4" />
                              View Call Details
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => lead.id && handleDeleteLead(lead.id)}>
                            <Trash className="mr-2 h-4 w-4" />
                            Delete Lead
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default LeadTable;
